import * as React from 'react';
import {
  Alert,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RoundedPaper from 'shared/components/RoundedPaper';
import {
  AccountAssessment,
  AccountHealth,
  AccountHealthRow,
  AccountStatus,
  WindowKey,
} from '../types';
import { WINDOW_ORDER, dash, fmtMoney, fmtRoas } from '../utils';

const TRUE_TARGET = 2.5;

// The narrative wraps ad/campaign names in **double asterisks** (markdown
// bold) — render those spans bold; everything else stays plain text.
function renderBold(text: string): React.ReactNode {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part,
  );
}
const WINDOW_HEADERS: Record<WindowKey, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  d7: '7d',
  d14: '14d',
};

const STATUS_META: Record<
  AccountStatus,
  { label: string; color: 'success' | 'warning' | 'error' | 'info' }
> = {
  outperforming: { label: 'Outperforming', color: 'success' },
  on_track: { label: 'On track', color: 'success' },
  below_target: { label: 'Below target', color: 'warning' },
  at_risk: { label: 'At risk', color: 'error' },
};

function Column({
  title,
  items,
  color,
}: {
  title: string;
  items?: string[];
  color?: string;
}) {
  if (!items || items.length === 0) return null;
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, color: color ?? 'text.secondary', letterSpacing: 0.3 }}
      >
        {title}
      </Typography>
      <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.2 }}>
        {items.map((it, i) => (
          <Typography
            key={i}
            component="li"
            variant="body2"
            sx={{ mb: 0.5, lineHeight: 1.35 }}
          >
            {renderBold(it)}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

function Assessment({ a }: { a: AccountAssessment }) {
  const sm = a.status ? STATUS_META[a.status] : undefined;
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        {sm && <Chip size="small" color={sm.color} label={sm.label} />}
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {renderBold(a.headline)}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Column title="✅ WHAT'S WORKING" items={a.working} color="var(--color-text-success, #1d7a4d)" />
        <Column title="⚠️ WHAT'S COSTING US" items={a.dragging} color="var(--color-text-danger, #b3261e)" />
        <Column title="🎯 WHAT TO LOOK INTO" items={a.focus} color="text.primary" />
      </Box>
      {a.watch_outs && a.watch_outs.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1.5, fontStyle: 'italic' }}
        >
          👁 {renderBold(a.watch_outs.join(' · '))}
        </Typography>
      )}
    </Box>
  );
}

export default function AccountHealthCards({
  health,
}: {
  health?: AccountHealth;
}) {
  if (!health) return null;
  const byWindow = (w: WindowKey): AccountHealthRow | undefined =>
    health.rows.find(r => r.window === w);

  const cell = (
    w: WindowKey,
    pick: (r: AccountHealthRow) => React.ReactNode,
  ) => {
    const r = byWindow(w);
    return (
      <TableCell key={w} align="right">
        {r ? pick(r) : dash}
      </TableCell>
    );
  };

  return (
    <RoundedPaper sx={{ p: 2, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Account Health
      </Typography>

      {health.assessment ? (
        <Assessment a={health.assessment} />
      ) : (
        health.umbrella_takeaway && (
          <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
            {health.umbrella_takeaway}
          </Alert>
        )
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell />
            {WINDOW_ORDER.map(w => (
              <TableCell key={w} align="right" sx={{ fontWeight: 500 }}>
                {WINDOW_HEADERS[w]}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell sx={{ color: 'text.secondary' }}>Spend</TableCell>
            {WINDOW_ORDER.map(w => cell(w, r => fmtMoney(r.spend)))}
          </TableRow>
          <TableRow>
            <TableCell sx={{ color: 'text.secondary' }}>True ROAS</TableCell>
            {WINDOW_ORDER.map(w =>
              cell(w, r => (
                <span
                  style={{
                    fontWeight: 600,
                    color:
                      (r.true_roas ?? 0) >= TRUE_TARGET
                        ? 'var(--color-text-success, #1d7a4d)'
                        : 'inherit',
                  }}
                >
                  {fmtRoas(r.true_roas)}
                </span>
              )),
            )}
          </TableRow>
          <TableRow>
            <TableCell sx={{ color: 'text.secondary' }}>Meta</TableCell>
            {WINDOW_ORDER.map(w => cell(w, r => fmtRoas(r.meta_roas)))}
          </TableRow>
          <TableRow>
            <TableCell sx={{ color: 'text.secondary' }}>WM last click</TableCell>
            {WINDOW_ORDER.map(w => cell(w, r => fmtRoas(r.wm_last_click)))}
          </TableRow>
          <TableRow>
            <TableCell sx={{ color: 'text.secondary' }}>WM incremental</TableCell>
            {WINDOW_ORDER.map(w => cell(w, r => fmtRoas(r.wm_incrementality)))}
          </TableRow>
        </TableBody>
      </Table>
    </RoundedPaper>
  );
}
